import React from 'react';
import {
  RefreshCw,
  ArrowLeft,
  Download,
  MapPin,
  Building,
  FileText,
  DollarSign,
  Activity,
  TrendingUp,
  AlertTriangle,
  Map,
} from 'lucide-react';
import { EnhancedMandalStats, EnhancedDistrictStats } from '../../types/districts.types';
import { formatCurrency, formatNumber, formatPercentage } from '../../utils/districts.utils';
import { masterDataAPI } from '../../services/master-data-api.service';
import { MandalType } from '../../types/master-data.types';

interface MandalsTableProps {
  mandals: EnhancedMandalStats[];
  loading: boolean;
  onBack: () => void;
  selectedDistrict: EnhancedDistrictStats;
}

const MandalsTable: React.FC<MandalsTableProps> = ({
  mandals,
  loading,
  onBack,
  selectedDistrict,
}) => {
  const [error, setError] = React.useState<string | null>(null);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-8 text-center">
          <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading mandals...</p>
        </div>
      </div>
    );
  }

  if (mandals.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-12 text-center">
          <Map className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Mandals Found</h3>
          <p className="text-gray-600">No mandals found for {selectedDistrict.name}.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Display */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="absolute top-0 right-0 px-4 py-3"
            aria-label="Close error message"
          >
            X
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={onBack}
              className="mr-4 p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Back to districts"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {selectedDistrict.name} - Mandals
              </h1>
              <p className="text-gray-600">
                {mandals.length} mandals • {formatNumber(selectedDistrict.total_works)} total works
              </p>
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={async () => {
                try {
                  await masterDataAPI.downloadExportData(
                    `mandals_${selectedDistrict.code}_${new Date().toISOString().split('T')[0]}.json`
                  );
                } catch (error) {
                  setError(error instanceof Error ? error.message : 'Failed to export mandals');
                }
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
              aria-label="Export mandals data"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Mandals
            </button>
          </div>
        </div>
      </div>

      {/* District Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Works</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(selectedDistrict.total_works)}</p>
            </div>
            <FileText className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Value</p>
              <p className="text-lg font-bold text-gray-900">
                {formatCurrency(selectedDistrict.total_estimate_cost)}
              </p>
            </div>
            <DollarSign className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Financial Progress</p>
              <p className="text-lg font-bold text-gray-900">
                {formatPercentage(selectedDistrict.financial_progress_percentage || 0)}
              </p>
            </div>
            <Activity className="w-8 h-8 text-orange-500" />
          </div>
          <div className="mt-2">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(selectedDistrict.financial_progress_percentage || 0, 100)}%` }}
              ></div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Performance Score</p>
              <p className="text-lg font-bold text-gray-900">
                {selectedDistrict.performance_score}/100
              </p>
            </div>
            <TrendingUp className="w-8 h-8 text-purple-500" />
          </div>
          <div className="mt-2">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-purple-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${selectedDistrict.performance_score}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Mandals Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" role="grid" aria-label="Mandals List">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  scope="col"
                >
                  Mandal
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  scope="col"
                >
                  Type
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  scope="col"
                >
                  Works
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  scope="col"
                >
                  Total Value
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  scope="col"
                >
                  Progress
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  scope="col"
                >
                  Status
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  scope="col"
                >
                  Performance
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {mandals.map((mandal) => (
                <tr key={mandal.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div
                          className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            mandal.mandal_type === MandalType.URBAN
                              ? 'bg-purple-100 text-purple-600'
                              : 'bg-green-100 text-green-600'
                          }`}
                        >
                          {mandal.mandal_type === MandalType.URBAN ? (
                            <Building className="w-4 h-4" />
                          ) : (
                            <MapPin className="w-4 h-4" />
                          )}
                        </div>
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">{mandal.name}</div>
                        <div className="text-sm text-gray-500">{mandal.code}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        mandal.mandal_type === MandalType.URBAN
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-green-100 text-green-800'
                      }`}
                    >
                      {mandal.mandal_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      <div className="font-semibold text-gray-900">{formatNumber(mandal.total_works)}</div>
                      <div className="text-gray-500 text-xs">
                        <span className="text-green-600">{mandal.completed_works || 0} completed</span>
                        {mandal.delayed_works > 0 && (
                          <span className="text-red-600 ml-2">
                            <AlertTriangle className="w-3 h-3 inline mr-1" />
                            {mandal.delayed_works} delayed
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      <div className="font-semibold text-gray-900">
                        {formatCurrency(mandal.total_estimate_cost)}
                      </div>
                      <div className="text-gray-500 text-xs">
                        Spent: {formatCurrency(mandal.total_expenditure || 0)}
                      </div>
                      <div className="text-blue-600 text-xs">
                        Avg: {formatCurrency(mandal.avg_estimate_cost || 0)}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-600">Financial</span>
                        <span className="font-medium">
                          {formatPercentage(mandal.financial_progress_percentage || 0)}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-green-400 to-green-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${Math.min(mandal.financial_progress_percentage || 0, 100)}%` }}
                        ></div>
                      </div>
                      {(mandal.physical_progress_percentage || 0) > 0 && (
                        <>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-600">Physical</span>
                            <span className="font-medium">
                              {formatPercentage(mandal.physical_progress_percentage || 0)}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-gradient-to-r from-blue-400 to-blue-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${Math.min(mandal.physical_progress_percentage || 0, 100)}%` }}
                            ></div>
                          </div>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-1">
                      {mandal.in_progress_works > 0 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {mandal.in_progress_works} Active
                        </span>
                      )}
{(mandal.completed_works || 0) > 0 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {mandal.completed_works} Done
                        </span>
                      )}
                      {mandal.delayed_works > 0 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          {mandal.delayed_works} Delayed
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-600">Utilization</span>
                        <span className="font-medium">
                          {formatPercentage(mandal.budget_utilization_percentage || 0)}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full transition-all duration-300 ${
                            (mandal.budget_utilization_percentage || 0) >= 75
                              ? 'bg-green-500'
                              : (mandal.budget_utilization_percentage || 0) >= 50
                              ? 'bg-yellow-500'
                              : 'bg-red-500'
                          }`}
                          style={{ width: `${Math.min(mandal.budget_utilization_percentage || 0, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MandalsTable;